import { ApolloClient, DocumentNode } from '@apollo/client';
import { graphql } from '../../../gql';
import { safeParseJson } from '../../document-revisions/safeJsonParser';

const query = graphql(/* GraphQL */ `
  query GetDocumentRevisions($documentId: ID!) {
    getDocumentById(id: $documentId) {
      id
      documentRevisions {
        attributes
        revisionId
        authorsIds
        createdAt
        creatorId
        fromVersion
        toVersion
        name
      }
    }
  }
`);

export const getDocumentRevisions =
  (documentId: string) =>
  (client: ApolloClient<object>): Promise<any[]> => {
    const variables = { documentId };

    return client
      .query({
        query: query as DocumentNode,
        variables,
        fetchPolicy: 'network-only',
      })
      .then(({ data }: Record<any, any>) => {
        const revisions = data.getDocumentById.documentRevisions;

        if (!revisions || revisions.length === 0) {
          return [];
        }

        // Map to CKEditor's expected format matching v41 pattern
        const mapped = revisions
          .map((revision: any) => ({
            id: revision.revisionId,
            name: revision.name || null,
            creatorId: revision.creatorId || 'system', // ✅ Provide default to prevent null issues
            authorsIds: Array.isArray(revision.authorsIds) ? revision.authorsIds : [],
            attributes:
              typeof revision.attributes === 'string'
                ? safeParseJson(revision.attributes, {})
                : revision.attributes || {},
            fromVersion: Number(revision.fromVersion || 0),
            toVersion: Number(revision.toVersion || 0),
            createdAt: revision.createdAt || new Date().toISOString(), // ✅ Ensure we have a timestamp
            // Omit data/diffData during initialization — the list loads first, bodies on demand.
            // CKEditor will load content on-demand via getRevision()
          }))
          .filter((r) => r.id); // ✅ Filter out any revisions without IDs

        // Sort by version (descending) for consistent display
        mapped.sort((a, b) => {
          if (b.toVersion !== a.toVersion) return b.toVersion - a.toVersion;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });

        return mapped;
      });
  };
